/**
 * A live site with none of its pages live: readers who open / get the bare
 * article list (smagent-sites HomeView), with no menu when the header has no
 * links left. Pages and Today warn about it, and Pages offers to restore what
 * was deleted.
 *
 * Only a site that has (or recently had) pages is warned. One that never had
 * any is an article list on purpose.
 */

export interface GapPage {
  state: { live?: unknown };
}

export interface GapDeleted {
  id: string;
  title: string;
  path: string | null;
  deletedAt: string;
  daysLeft: number;
}

export interface HomeGap {
  /** false: the header has no links either. null: not known yet. */
  menu: boolean | null;
  /** Deleted pages that can still be restored. */
  restorable: number;
}

export function homeGap({
  publicEnabled,
  pages,
  deleted,
  navigation,
}: {
  publicEnabled: boolean | null | undefined;
  pages: GapPage[];
  deleted: GapDeleted[];
  navigation?: { header?: unknown[] | null; cta?: unknown } | null;
}): HomeGap | null {
  if (!publicEnabled) return null;
  if (pages.some((p) => p.state?.live)) return null;
  if (!pages.length && !deleted.length) return null;
  return {
    menu: navigation ? Boolean(navigation.header?.length || navigation.cta) : null,
    restorable: deleted.length,
  };
}

/** "Your latest articles, with no menu." — what a reader sees at / right now. */
export function readersSee(gap: HomeGap) {
  return `your latest articles${gap.menu === false ? ', with no menu' : ''}`;
}

const shortDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/**
 * "Home, About and 7 more were deleted on Oct 9. You can restore them for 29
 * more days." The days are the soonest to run out.
 */
export function deletedSentence(items: GapDeleted[]) {
  if (!items.length) return '';
  const titles = items.map((d) => d.title || 'Untitled');
  const names =
    titles.length === 1
      ? titles[0]
      : titles.length <= 3
        ? `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`
        : `${titles.slice(0, 2).join(', ')} and ${titles.length - 2} more`;
  const days = items.map((d) => shortDay(d.deletedAt));
  const sorted = [...items].sort(
    (a, b) => new Date(a.deletedAt).getTime() - new Date(b.deletedAt).getTime()
  );
  const first = shortDay(sorted[0].deletedAt);
  const last = shortDay(sorted[sorted.length - 1].deletedAt);
  const when = new Set(days).size === 1 ? `on ${first}` : `between ${first} and ${last}`;
  const one = items.length === 1;
  const left = Math.min(...items.map((d) => d.daysLeft));
  const restore =
    left <= 0
      ? `Today is the last day to restore ${one ? 'it' : 'them'}.`
      : `You can restore ${one ? 'it' : 'them'} for ${left} more day${left === 1 ? '' : 's'}.`;
  return `${names} ${one ? 'was' : 'were'} deleted ${when}. ${restore}`;
}

/** Parents before the pages inside them: the server refuses a page whose parent is still deleted. */
export function restoreOrder<T extends { path: string | null }>(items: T[]): T[] {
  const depth = (p: string | null) => (p || '').split('/').filter(Boolean).length;
  return [...items].sort((a, b) => depth(a.path) - depth(b.path));
}
